import type {
  AnalyticsData,
  Attachment,
  CustomerRecord,
  MailboxRecord,
  RuntimeConfig,
  SecurityIncident,
  SecurityIncidentList,
  SecurityRule,
  StaffRecord,
  Ticket,
  TicketMessage,
} from "./types";

import { browserWorkspace, type BrowserWorkspace } from "./workspace";

const csrfTokens = new Map<BrowserWorkspace, string>();

type UnknownRecord = Record<string, unknown>;

function record(value: unknown): UnknownRecord {
  return value && typeof value === "object" ? value as UnknownRecord : {};
}

function array(value: unknown): unknown[] {
  return Array.isArray(value) ? value : [];
}

export function rememberSecurityTokens(value: unknown, workspace = browserWorkspace()) {
  const source = record(value);
  if (typeof source.csrfToken === "string") csrfTokens.set(workspace, source.csrfToken);
}

export function activeCsrfToken(workspace = browserWorkspace()) {
  return csrfTokens.get(workspace);
}

export function clearSecurityTokens() {
  csrfTokens.clear();
}

export function isAnonymousSession(path: string, value: unknown) {
  return path.includes("/auth/") && path.split("?")[0].endsWith("/session") && record(value).authenticated === false;
}

export function normalizeRuntimeConfig(value: unknown): RuntimeConfig {
  const source = record(value);
  const attachment = record(source.attachment);
  return {
    ...(source as RuntimeConfig),
    maxAttachmentBytes: Number(attachment.maxBytes || source.maxAttachmentBytes || 25 * 1024 * 1024),
    push: {
      ...record(source.push),
      vapidPublicKey: String(record(source.push).vapidPublicKey || source.vapidPublicKey || "") || undefined,
    },
  };
}

function normalizeAttachment(value: unknown): Attachment {
  const source = record(value);
  const scan = String(source.scanStatus || "pending");
  return {
    id: String(source.id || ""),
    name: String(source.name || source.filename || "attachment"),
    size: Number(source.size || source.sizeBytes || 0),
    contentType: typeof source.contentType === "string" ? source.contentType : undefined,
    downloadUrl: typeof source.downloadUrl === "string" ? source.downloadUrl : undefined,
    scanStatus: scan as Attachment["scanStatus"],
    scanBypassed: source.scanBypassed === true,
  };
}

function normalizeMessage(value: unknown): TicketMessage {
  const source = record(value);
  const author = record(source.author);
  const authorType = String(source.authorType || author.type || "system");
  return {
    id: String(source.id || ""),
    author: {
      id: typeof author.id === "string" ? author.id : undefined,
      name: String(author.name || "Next-Cart Support"),
      email: typeof author.email === "string" ? author.email : undefined,
      avatarUrl: typeof author.avatarUrl === "string" ? author.avatarUrl : undefined,
      type: authorType === "integration" ? "ai" : authorType as "customer" | "staff" | "system",
    },
    visibility: source.visibility === "internal" ? "internal" : "public",
    cc: source.visibility === "internal" ? [] : array(source.cc).filter((email): email is string => typeof email === "string"),
    body: String(source.body || source.text || ""),
    bodyHtml: typeof source.bodyHtml === "string" ? source.bodyHtml : typeof source.html === "string" ? source.html : undefined,
    createdAt: String(source.createdAt || new Date().toISOString()),
    attachments: array(source.attachments).map(normalizeAttachment),
    channel: typeof source.channel === "string" ? source.channel : undefined,
    deliveryStatus: typeof source.deliveryStatus === "string" ? source.deliveryStatus as TicketMessage["deliveryStatus"] : undefined,
  };
}

const sensitiveContextKey = /(?:password|passwd|secret|token|api[_-]?key|authorization|cookie|session|card|cvv)/i;

function sanitizeContextValue(value: unknown, depth = 0): unknown {
  if (value === null || typeof value === "boolean" || typeof value === "number") return value;
  if (typeof value === "string") return value.slice(0, 1000);
  if (depth >= 2) return "[nested data]";
  if (Array.isArray(value)) return value.slice(0, 20).map((item) => sanitizeContextValue(item, depth + 1));
  if (value && typeof value === "object") {
    return Object.fromEntries(Object.entries(value as UnknownRecord)
      .filter(([key]) => !sensitiveContextKey.test(key))
      .slice(0, 30)
      .map(([key, item]) => [key.slice(0, 80), sanitizeContextValue(item, depth + 1)]));
  }
  return String(value).slice(0, 1000);
}

function normalizeContext(value: unknown): Record<string, unknown> {
  if (!value || typeof value !== "object" || Array.isArray(value)) return {};
  return sanitizeContextValue(value) as Record<string, unknown>;
}

function normalizeRelatedObjects(source: UnknownRecord, context: UnknownRecord): Ticket["relatedObjects"] {
  const items: NonNullable<Ticket["relatedObjects"]> = [];
  const seen = new Set<string>();
  const add = (typeValue: unknown, idValue: unknown, labelValue?: unknown) => {
    const type = typeof typeValue === "string" ? typeValue.trim().slice(0, 80) : "";
    const id = typeof idValue === "string" || typeof idValue === "number" ? String(idValue).trim().slice(0, 255) : "";
    if (!type || !id || seen.has(`${type}:${id}`)) return;
    seen.add(`${type}:${id}`);
    items.push({ type, id, label: typeof labelValue === "string" ? labelValue.slice(0, 255) : undefined });
  };

  for (const item of array(source.relatedObjects)) {
    const candidate = record(item);
    add(candidate.type, candidate.id, candidate.label);
  }

  const related = record(source.related);
  const nestedRelated = record(context.related);
  const definitions = [
    ["order", "orderId", "order_id", "relatedOrderId", "related_order_id"],
    ["migration", "migrationId", "migration_id", "relatedMigrationId", "related_migration_id"],
    ["license", "licenseId", "license_id", "relatedLicenseId", "related_license_id"],
  ] as const;
  for (const [type, camel, snake, sourceCamel, sourceSnake] of definitions) {
    add(type, related[camel] ?? related[snake] ?? nestedRelated[camel] ?? nestedRelated[snake]
      ?? context[camel] ?? context[snake] ?? source[sourceCamel] ?? source[sourceSnake]);
  }
  return items;
}

export function normalizeTicket(value: unknown): Ticket {
  const source = record(value);
  const quarantineTimestamp = Object.hasOwn(source, "quarantinedAt") ? source.quarantinedAt : source.quarantined_at;
  const quarantinedAt = typeof quarantineTimestamp === "string" && quarantineTimestamp ? quarantineTimestamp : null;
  const quarantineType = source.quarantineType ?? source.quarantine_type;
  const context = normalizeContext(source.context);
  const rawSla = record(source.sla);
  const rawAgentNote = record(source.agentNote);
  const rawAgentNoteAuthor = record(rawAgentNote.updatedBy);
  const dueAt = Object.hasOwn(rawSla, "responseDueAt")
    ? rawSla.responseDueAt || rawSla.resolutionDueAt
    : rawSla.firstResponseDueAt || rawSla.resolutionDueAt;
  const dueDate = typeof dueAt === "string" ? new Date(dueAt) : null;
  const slaState = rawSla.breached
    ? "breached"
    : dueDate && dueDate.getTime() - Date.now() < 2 * 60 * 60 * 1000
      ? "warning"
      : "ok";
  return {
    ...(source as unknown as Ticket),
    id: String(source.id || ""),
    number: String(source.number || ""),
    subject: String(source.subject || "Untitled ticket"),
    status: (source.status || "new") as Ticket["status"],
    priority: (source.priority || "normal") as Ticket["priority"],
    quarantineType: quarantinedAt ? quarantineType === "spam" ? "spam" : "security" : null,
    quarantineReason: typeof (source.quarantineReason || source.quarantine_reason) === "string" ? String(source.quarantineReason || source.quarantine_reason) : null,
    quarantinedAt,
    channel: String(source.channel || source.source || "web"),
    requester: record(source.requester) as unknown as Ticket["requester"],
    createdAt: String(source.createdAt || new Date().toISOString()),
    updatedAt: String(source.lastActivityAt || source.updatedAt || source.createdAt || new Date().toISOString()),
    messages: Array.isArray(source.messages) ? source.messages.map(normalizeMessage) : undefined,
    collaborators: array(source.collaborators).map((value) => ({
      email: String(record(value).email || ""),
      name: typeof record(value).name === "string" ? String(record(value).name) : null,
    })).filter((value) => value.email),
    messagePage: source.messagePage && typeof source.messagePage === "object" ? {
      hasMore: Boolean(record(source.messagePage).hasMore),
      nextCursor: typeof record(source.messagePage).nextCursor === "string" ? String(record(source.messagePage).nextCursor) : null,
      limit: Number(record(source.messagePage).limit || 10),
    } : undefined,
    emailFollowupTicketId: typeof source.emailFollowupTicketId === "string" ? source.emailFollowupTicketId : null,
    agentNote: Object.keys(rawAgentNote).length ? {
      body: String(rawAgentNote.body || ""),
      bodyHtml: typeof rawAgentNote.bodyHtml === "string" ? rawAgentNote.bodyHtml : undefined,
      revision: typeof rawAgentNote.revision === "number" ? rawAgentNote.revision : undefined,
      updatedAt: typeof rawAgentNote.updatedAt === "string" ? rawAgentNote.updatedAt : null,
      updatedBy: rawAgentNoteAuthor.id ? {
        id: String(rawAgentNoteAuthor.id),
        name: String(rawAgentNoteAuthor.name || "Staff member"),
      } : null,
    } : undefined,
    context,
    relatedObjects: normalizeRelatedObjects(source, context),
    sla: Object.keys(rawSla).length ? {
      state: slaState,
      dueAt: typeof dueAt === "string" ? dueAt : undefined,
      label: rawSla.breached ? "SLA breached" : undefined,
    } : undefined,
  };
}

function looksLikeTicket(value: unknown) {
  const source = record(value);
  return typeof source.id === "string" && typeof source.subject === "string" && typeof source.status === "string";
}

function normalizeCustomer(value: unknown): CustomerRecord {
  const source = record(value);
  return {
    id: String(source.id || ""),
    externalId: typeof source.externalId === "string" ? source.externalId : typeof source.external_id === "string" ? source.external_id : undefined,
    sourceSystem: typeof source.sourceSystem === "string" ? source.sourceSystem : typeof source.source_system === "string" ? source.source_system : undefined,
    name: String(source.name || "Unknown customer"),
    email: typeof source.email === "string" ? source.email : undefined,
    phone: typeof source.phone === "string" ? source.phone : undefined,
    status: typeof source.status === "string" ? source.status : undefined,
    spamMarked: Boolean(source.spamMarked ?? source.spam_marked ?? source.spam_marked_at),
    spamMarkedAt: typeof source.spamMarkedAt === "string" ? source.spamMarkedAt : typeof source.spam_marked_at === "string" ? source.spam_marked_at : null,
    spamReason: typeof source.spamReason === "string" ? source.spamReason : typeof source.spam_reason === "string" ? source.spam_reason : null,
    openTickets: Number(source.openTickets || source.open_tickets || 0),
    totalTickets: Number(source.totalTickets || source.ticket_count || 0),
    lastSeenAt: typeof source.lastSeenAt === "string" ? source.lastSeenAt : source.last_seen_at instanceof Date ? source.last_seen_at.toISOString() : typeof source.last_seen_at === "string" ? source.last_seen_at : undefined,
    locale: typeof source.locale === "string" ? source.locale : undefined,
    timezone: typeof source.timezone === "string" ? source.timezone : undefined,
    createdAt: typeof source.createdAt === "string" ? source.createdAt : typeof source.created_at === "string" ? source.created_at : undefined,
    updatedAt: typeof source.updatedAt === "string" ? source.updatedAt : typeof source.updated_at === "string" ? source.updated_at : undefined,
  };
}

function normalizeStaff(value: unknown): StaffRecord {
  const source = record(value);
  return {
    id: String(source.id || ""),
    name: String(source.name || source.displayName || source.display_name || "Staff member"),
    email: String(source.email || ""),
    status: String(source.status || "active"),
    roles: array(source.roles).map(String),
    teams: array(source.teams).map(String),
    teamIds: array(source.teamIds || source.team_ids).map(String),
    timezone: String(source.timezone || "Asia/Ho_Chi_Minh"),
    mfaRequired: Boolean(source.mfaRequired ?? source.mfa_required ?? true),
    lastLoginAt: typeof source.lastLoginAt === "string" ? source.lastLoginAt : typeof source.last_login_at === "string" ? source.last_login_at : undefined,
    passwordChangeRequired: Boolean(source.passwordChangeRequired ?? source.password_change_required),
  };
}

function normalizeMailbox(value: unknown): MailboxRecord {
  const source = record(value);
  const address = source.address || (source.local_part && source.domain ? `${source.local_part}@${source.domain}` : "");
  return {
    id: String(source.id || ""),
    name: String(source.name || "Mailbox"),
    address: String(address),
    aliases: array(source.aliases).filter((alias): alias is string => typeof alias === "string"),
    outboundName: String(source.outboundName || source.display_name || "Next-Cart Support"),
    replyToDomain: String(source.replyToDomain || source.reply_to_domain || ""),
    defaultTeamId: typeof source.defaultTeamId === "string" ? source.defaultTeamId : typeof source.default_team_id === "string" ? source.default_team_id : undefined,
    status: source.active === false ? "disabled" : "active",
    autoReplyEnabled: Boolean(source.autoReplyEnabled ?? source.auto_response_enabled ?? true),
    collaboratorNotificationsEnabled: Boolean(source.collaboratorNotificationsEnabled ?? source.collaborator_notifications_enabled ?? true),
    receiptSubjectTemplate: String(source.receiptSubjectTemplate || source.receipt_subject_template || ""),
    receiptBodyTemplate: String(source.receiptBodyTemplate || source.receipt_body_template || ""),
    collaboratorSubjectTemplate: String(source.collaboratorSubjectTemplate || source.collaborator_subject_template || ""),
    collaboratorBodyTemplate: String(source.collaboratorBodyTemplate || source.collaborator_body_template || ""),
  };
}

function normalizeSecurityRule(value: unknown): SecurityRule {
  const source = record(value);
  const conditions = record(source.conditions);
  const ruleType = source.ruleType || source.rule_type;
  const action = String(source.action || "quarantine") as SecurityRule["action"];
  const score = Number(source.score);
  return {
    id: String(source.id || ""),
    name: String(source.name || source.reason || "Security rule"),
    kind: (source.kind || source.entity_type || ruleType || conditions.kind || "content") as SecurityRule["kind"],
    value: String(source.value || source.entity_value || conditions.value || JSON.stringify(conditions)),
    displayValue: String(source.matched_customer_email || source.value || source.entity_value || conditions.value || JSON.stringify(conditions)),
    customerName: typeof source.matched_customer_name === "string" ? source.matched_customer_name : undefined,
    action,
    enabled: Boolean(source.enabled ?? source.active ?? true),
    ruleType: ruleType === "spam" || ruleType === "sender_auth" ? ruleType : undefined,
    priority: Number.isFinite(Number(source.priority)) ? Number(source.priority) : undefined,
    conditions: conditions as SecurityRule["conditions"],
    score: Number.isFinite(score) ? score : undefined,
    createdAt: typeof source.created_at === "string" ? source.created_at : undefined,
  };
}

function normalizeSecurityIncident(value: unknown): SecurityIncident {
  const source = record(value);
  const ticket = record(source.ticket);
  const resolvedBy = record(source.resolvedBy);
  const ticketId = ticket.id || source.ticket_id;
  return {
    id: String(source.id || ""),
    incidentType: String(source.incidentType || source.incident_type || "security_incident"),
    severity: String(source.severity || "info") as SecurityIncident["severity"],
    source: String(source.source || "system"),
    reason: String(source.reason || source.incidentType || source.incident_type || "security_incident"),
    details: record(source.details),
    resolvedBy: resolvedBy.id ? { id: String(resolvedBy.id), name: String(resolvedBy.name || "Staff member"),
      email: typeof resolvedBy.email === "string" ? resolvedBy.email : null } : null,
    resolution: typeof source.resolution === "string" ? source.resolution : null,
    resolvedAt: typeof source.resolvedAt === "string" ? source.resolvedAt : typeof source.resolved_at === "string" ? source.resolved_at : null,
    createdAt: String(source.createdAt || source.created_at || new Date().toISOString()),
    ticket: ticketId ? {
      id: String(ticketId),
      publicId: typeof ticket.publicId === "string" ? ticket.publicId : typeof source.ticket_public_id === "string" ? source.ticket_public_id : null,
      number: typeof ticket.number === "string" ? ticket.number : typeof source.ticket_number === "string" ? source.ticket_number : null,
      quarantinedAt: typeof ticket.quarantinedAt === "string" ? ticket.quarantinedAt : null,
      quarantineType: ticket.quarantineType === "spam" || ticket.quarantineType === "security" ? ticket.quarantineType : null,
    } : null,
  };
}

function normalizeSecurityIncidentList(value: unknown): SecurityIncidentList {
  const source = record(value);
  const incidents = array(source.incidents).map(normalizeSecurityIncident);
  return {
    incidents,
    page: Math.max(1, Number(source.page || 1)),
    limit: Math.max(1, Number(source.limit || 25)),
    total: Math.max(0, Number(source.total || incidents.length)),
  };
}

function normalizeAnalytics(value: unknown): AnalyticsData {
  const source = record(value);
  const series = array(source.series).map(record);
  const responseValues = series.map((item) => Number(item.first_response_minutes || 0)).filter(Boolean);
  return {
    firstResponseMinutes: typeof source.firstResponseMinutes === "number" ? source.firstResponseMinutes : responseValues.length ? Math.round(responseValues.reduce((sum, item) => sum + item, 0) / responseValues.length) : 0,
    resolutionHours: Number(source.resolutionHours || 0),
    slaPercent: Number(source.slaPercent || 0),
    satisfactionPercent: Number(source.satisfactionPercent || 0),
    volume: series.map((item) => ({ label: String(item.day || "").slice(5), value: Number(item.created || 0) })),
    channels: Array.isArray(source.channels) ? source.channels as AnalyticsData["channels"] : undefined,
    sources: Array.isArray(source.sources) ? source.sources as AnalyticsData["sources"] : undefined,
    ticketCount: Number(source.ticketCount || 0),
  };
}

export function normalizeApiData(path: string, value: unknown): unknown {
  const source = record(value);
  // Export DTOs deliberately omit normal ticket/contact fields and keep every
  // public message. Do not adapt them to the paginated interactive ticket model.
  if (/^\/agent\/tickets\/[^/]+\/export-conversation(?:\?|$)/.test(path)) return value;
  if (path === "/config") return normalizeRuntimeConfig(value);
  if (path.includes("/attachments") && Array.isArray(source.attachments)) {
    const attachments = source.attachments.map(normalizeAttachment);
    return attachments.length === 1 ? { attachment: attachments[0], attachments } : { attachments };
  }
  if (path.includes("/agent/dashboard")) {
    const counts = record(source.counts);
    const sla = record(source.sla);
    const recent = array(source.myTickets).map(normalizeTicket);
    return {
      counts: {
        unassigned: Number(counts.unassigned || counts.new || 0),
        mine: recent.filter((ticket) => !["solved", "closed"].includes(ticket.status)).length,
        urgent: Number(counts.urgent || 0),
        slaRisk: Number(sla.approaching || 0) + Number(sla.breached || 0),
      },
      recentTickets: recent,
      firstResponsePercent: Number(source.firstResponsePercent || 100),
    };
  }
  if (path.includes("/agent/analytics")) return normalizeAnalytics(value);
  if (path.includes("/agent/customers") && source.customer) return { ...source, customer: normalizeCustomer(source.customer) };
  if (path.includes("/agent/customers") && Array.isArray(source.customers)) return { ...source, customers: source.customers.map(normalizeCustomer) };
  if (path.includes("/admin/staff") && Array.isArray(source.staff)) return { ...source, staff: source.staff.map(normalizeStaff) };
  if (path.includes("/admin/roles") && Array.isArray(source.roles)) {
    return { ...source, roles: source.roles.map((role) => ({ ...record(role), id: String(record(role).id || ""), code: String(record(role).code || "") })) };
  }
  if (path.includes("/admin/mailboxes") && Array.isArray(source.mailboxes)) return { ...source, mailboxes: source.mailboxes.map(normalizeMailbox) };
  if (path.includes("/admin/security/rules") && Array.isArray(source.rules)) return { ...source, rules: source.rules.map(normalizeSecurityRule) };
  if (path.includes("/admin/security/blocks") && Array.isArray(source.blocks)) return { ...source, blocks: source.blocks.map(normalizeSecurityRule) };
  if (path.includes("/admin/security/quarantine") && Array.isArray(source.tickets)) return source;
  if (path.includes("/admin/security/incidents") && Array.isArray(source.incidents)) return normalizeSecurityIncidentList(source);
  if (source.ticket) return { ...source, ticket: normalizeTicket(source.ticket) };
  if (Array.isArray(source.tickets)) return { ...source, tickets: source.tickets.map(normalizeTicket) };
  if (Array.isArray(value) && (value.length === 0 || looksLikeTicket(value[0]))) return value.map(normalizeTicket);
  if (looksLikeTicket(value)) return normalizeTicket(value);
  return value;
}

export function adaptApiPath(path: string) {
  const url = new URL(path, "https://support.invalid");
  if (url.searchParams.has("query")) {
    url.searchParams.set("q", url.searchParams.get("query") || "");
    url.searchParams.delete("query");
  }
  if (url.pathname.includes("/agent/tickets") && url.searchParams.has("assignee")) {
    const value = url.searchParams.get("assignee");
    if (value) url.searchParams.set("assigneeId", value);
    url.searchParams.delete("assignee");
  }
  url.searchParams.delete("cursor");
  return `${url.pathname}${url.search}`;
}

export function adaptJsonBody(path: string, method: string, value: unknown, headers?: Headers): unknown {
  const body = record(value);
  if (path === "/auth/staff/login" && method === "POST") {
    return {
      email: body.email,
      password: body.password,
      totp: body.mfaCode || body.totp,
      recoveryCode: body.recoveryCode,
    };
  }
  if (path === "/tickets" && method === "POST") {
    return { subject: body.subject, message: body.message || body.description, html: body.html || body.descriptionHtml, priority: body.priority, mailboxId: body.mailboxId, tags: body.tags || [], related: body.related || body.context, attachmentIds: body.attachmentIds || [], cc: body.cc || [] };
  }
  if (/\/tickets\/[^/]+\/messages$/.test(path) && method === "POST") {
    return { text: body.text || body.body, html: body.html, visibility: body.visibility, attachmentIds: body.attachmentIds || [], ...(body.cc !== undefined ? { cc: body.cc } : {}) };
  }
  if (/\/agent\/tickets\/[^/]+$/.test(path) && method === "PATCH") {
    const match = headers?.get("If-Match")?.match(/(\d+)/);
    return { ...body, version: body.version || (match ? Number(match[1]) : undefined) };
  }
  if (path === "/agent/tickets/bulk" && method === "POST") {
    if (body.action === "pending" || body.action === "solved") return { ticketIds: body.ticketIds, status: body.action };
    return body;
  }
  if (path === "/admin/staff" && method === "POST") {
    return { displayName: body.name, email: body.email, roleCodes: body.roleIds || [], teamIds: body.teamIds || [], status: body.status || "active", timezone: body.timezone || "Asia/Ho_Chi_Minh", mfaRequired: body.mfaRequired ?? true };
  }
  if (/^\/admin\/staff\/[^/]+$/.test(path) && method === "PATCH") {
    return { ...body, ...(body.name !== undefined ? { displayName: body.name } : {}), ...(body.roleIds !== undefined ? { roleCodes: body.roleIds } : {}) };
  }
  if ((path === "/admin/mailboxes" && method === "POST") || (/^\/admin\/mailboxes\/[^/]+$/.test(path) && method === "PATCH")) {
    const [localPart, domain] = String(body.address || "").split("@");
    const creating = method === "POST";
    return {
      ...(creating || body.name !== undefined ? { name: body.name } : {}),
      ...(creating || body.address !== undefined ? { localPart, domain, replyFromAddress: body.address } : {}),
      ...(creating || body.aliases !== undefined ? { aliases: body.aliases ?? [] } : {}),
      ...(creating || body.outboundName !== undefined ? { displayName: body.outboundName || body.name } : {}),
      ...(creating ? { defaultTeamId: body.defaultTeamId || null, defaultPriority: "normal" } : {}),
      ...(creating || body.replyToDomain !== undefined ? { replyToDomain: body.replyToDomain || domain } : {}),
      ...(creating || body.autoReplyEnabled !== undefined || body.autoResponseEnabled !== undefined ? { autoResponseEnabled: body.autoReplyEnabled ?? body.autoResponseEnabled ?? true } : {}),
      ...(creating || body.receiptSubjectTemplate !== undefined ? { receiptSubjectTemplate: body.receiptSubjectTemplate } : {}),
      ...(creating || body.receiptBodyTemplate !== undefined ? { receiptBodyTemplate: body.receiptBodyTemplate } : {}),
      ...(creating || body.collaboratorSubjectTemplate !== undefined ? { collaboratorSubjectTemplate: body.collaboratorSubjectTemplate } : {}),
      ...(creating || body.collaboratorBodyTemplate !== undefined ? { collaboratorBodyTemplate: body.collaboratorBodyTemplate } : {}),
      ...(creating || body.active !== undefined ? { active: body.active ?? true } : {}),
      ...(creating || body.collaboratorNotificationsEnabled !== undefined ? { collaboratorNotificationsEnabled: body.collaboratorNotificationsEnabled ?? true } : {}),
    };
  }
  if (path === "/admin/security/blocks" && method === "POST") {
    return { entityType: body.entityType || body.kind, entityValue: body.entityValue || body.value, reason: body.reason || body.name, active: true };
  }
  if (path === "/push/subscriptions" && body.subscription) {
    const subscription = record(body.subscription);
    return { endpoint: subscription.endpoint, expirationTime: subscription.expirationTime ?? null, keys: subscription.keys, deviceName: body.deviceName || "Web browser" };
  }
  return value;
}

export function adaptFormBody(path: string, value: BodyInit | undefined) {
  if (path !== "/attachments" || !(value instanceof FormData)) return value;
  const adapted = new FormData();
  value.forEach((entry) => adapted.append(entry instanceof File ? "files" : "field", entry));
  return adapted;
}
